// ---------------------------------------------------------------------------
// WCAG 2.1 contrast ratio helpers
//
// Pure functions, no React, no DOM. Intended for non-blocking accessibility
// advisories (e.g. brand color picker hints). The math follows the WCAG
// relative-luminance definition: https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
// ---------------------------------------------------------------------------

const HEX_SHORT = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const HEX_LONG = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})(?:[0-9a-f]{2})?$/i;

const WCAG_AA_NORMAL = 4.5;
const WCAG_AA_LARGE = 3;
const WCAG_AAA_NORMAL = 7;

export interface RgbColor {
  r: number;
  g: number;
  b: number;
}

/**
 * Parse a hex color string ("#fff", "#ffffff", "#ffffffff") into 0-255 RGB.
 * Returns null for unparseable input — never throws.
 */
export function parseHexColor(input: string): RgbColor | null {
  if (typeof input !== "string") {
    return null;
  }
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const long = trimmed.match(HEX_LONG);
  if (long) {
    return {
      r: Number.parseInt(long[1] ?? "0", 16),
      g: Number.parseInt(long[2] ?? "0", 16),
      b: Number.parseInt(long[3] ?? "0", 16),
    };
  }

  const short = trimmed.match(HEX_SHORT);
  if (short) {
    const [, r = "0", g = "0", b = "0"] = short;
    return {
      r: Number.parseInt(`${r}${r}`, 16),
      g: Number.parseInt(`${g}${g}`, 16),
      b: Number.parseInt(`${b}${b}`, 16),
    };
  }

  return null;
}

function channelToLinear(channel: number): number {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : Math.pow((normalized + 0.055) / 1.055, 2.4);
}

/**
 * Relative luminance per WCAG 2.1. Range: 0 (black) to 1 (white).
 */
export function relativeLuminance(color: RgbColor): number {
  const r = channelToLinear(color.r);
  const g = channelToLinear(color.g);
  const b = channelToLinear(color.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Contrast ratio between two hex colors per WCAG 2.1.
 * Returns a number in [1, 21]. Returns null if either color is unparseable.
 *
 * 4.5:1 = WCAG AA normal text
 * 3:1   = WCAG AA large text / non-text
 * 7:1   = WCAG AAA normal text
 */
export function contrastRatio(
  foreground: string,
  background: string,
): number | null {
  const fg = parseHexColor(foreground);
  const bg = parseHexColor(background);
  if (!fg || !bg) {
    return null;
  }

  const fgLum = relativeLuminance(fg);
  const bgLum = relativeLuminance(bg);
  const lighter = Math.max(fgLum, bgLum);
  const darker = Math.min(fgLum, bgLum);
  return (lighter + 0.05) / (darker + 0.05);
}

export type WcagLevel = "AAA" | "AA" | "AA-large" | "fail";

/**
 * Classify a contrast ratio against WCAG thresholds.
 */
export function classifyContrast(ratio: number | null): WcagLevel {
  if (ratio === null || !Number.isFinite(ratio)) {
    return "fail";
  }
  if (ratio >= WCAG_AAA_NORMAL) {
    return "AAA";
  }
  if (ratio >= WCAG_AA_NORMAL) {
    return "AA";
  }
  if (ratio >= WCAG_AA_LARGE) {
    return "AA-large";
  }
  return "fail";
}

/**
 * Convenience: does the pair meet WCAG AA for normal text (4.5:1)?
 */
export function meetsWcagAa(
  foreground: string,
  background: string,
): boolean {
  const ratio = contrastRatio(foreground, background);
  return ratio !== null && ratio >= WCAG_AA_NORMAL;
}

export const WCAG_THRESHOLDS = {
  AA_NORMAL: WCAG_AA_NORMAL,
  AA_LARGE: WCAG_AA_LARGE,
  AAA_NORMAL: WCAG_AAA_NORMAL,
} as const;
